const CONTAINER_SPACE_AREA_MEDIAN_MULTIPLIER = 8;
const CONTAINER_SPACE_MIN_CONTAINED_SPACES = 8;

const getValidPolygon2d = (polygon?: number[][] | null): number[][] | null => {
  if (!Array.isArray(polygon) || polygon.length < 3) {
    return null;
  }

  const validPoints = polygon.filter((point) => {
    const [x, y] = point ?? [];
    return (
      typeof x === "number" && typeof y === "number" && Number.isFinite(x) && Number.isFinite(y)
    );
  });

  return validPoints.length >= 3 ? validPoints : null;
};

export const getPolygonArea = (polygon?: number[][] | null): number => {
  const points = getValidPolygon2d(polygon);
  if (points == null) {
    return 0;
  }

  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    sum += x1 * y2 - x2 * y1;
  }

  return Math.abs(sum) / 2;
};

const getPolygonCenter = (polygon?: number[][] | null): [number, number] | null => {
  const points = getValidPolygon2d(polygon);
  if (points == null) {
    return null;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const [x, y] of points) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
    return null;
  }

  return [(minX + maxX) / 2, (minY + maxY) / 2];
};

const isPointInPolygon = ([x, y]: [number, number], polygon?: number[][] | null): boolean => {
  const points = getValidPolygon2d(polygon);
  if (points == null) {
    return false;
  }

  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, yi] = points[i];
    const [xj, yj] = points[j];
    const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

const getMedian = (values: number[]): number => {
  const sorted = values.filter((value) => value > 0).sort((a, b) => a - b);
  if (sorted.length === 0) {
    return 0;
  }

  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
};

export const filterOutContainerMcBimSpaces = <
  T extends { polygon?: number[][] | null; longName?: string | null; name?: string | null },
>(
  spaces: T[]
): T[] => {
  const spaceAreas = spaces.map((space) => getPolygonArea(space.polygon));
  const medianArea = getMedian(spaceAreas);

  if (medianArea === 0 || spaces.length < CONTAINER_SPACE_MIN_CONTAINED_SPACES + 1) {
    return spaces;
  }

  const spaceCenters = spaces.map((space) => getPolygonCenter(space.polygon));

  return spaces.filter((space, index) => {
    const area = spaceAreas[index];
    if (area <= medianArea * CONTAINER_SPACE_AREA_MEDIAN_MULTIPLIER) {
      return true;
    }

    let containedSpaces = 0;
    for (let otherIndex = 0; otherIndex < spaces.length; otherIndex++) {
      if (otherIndex === index) {
        continue;
      }

      const center = spaceCenters[otherIndex];
      if (center != null && isPointInPolygon(center, space.polygon)) {
        containedSpaces++;
      }
    }

    return containedSpaces < CONTAINER_SPACE_MIN_CONTAINED_SPACES;
  });
};
